import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Database, Loader2, MoreHorizontal, Plug, Plus, RefreshCcw, Server, Zap } from "lucide-react";
import {
  BusinessDataConnection,
  refreshBusinessDataConnection,
  useBusinessDataConnections,
} from "@/hooks/useBusinessDataConnections";
import BusinessDataConnectionDialog from "./BusinessDataConnectionDialog";
import PlanFeatureGate from "./PlanFeatureGate";
import { toast } from "sonner";

// "Business Data" section in connector settings. Lists every connection the
// business has set up plus an "Add data source" action.

function timeAgo(iso: string | null): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}

function TypePill({ type }: { type: BusinessDataConnection["connection_type"] }) {
  if (type === "url_feed") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        <Database className="w-3 h-3" />
        Live feed
      </Badge>
    );
  }
  if (type === "rest_api") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        <Zap className="w-3 h-3" />
        On-demand API
      </Badge>
    );
  }
  if (type === "rest_action") {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-emerald-300 text-emerald-700">
        <Zap className="w-3 h-3" />
        Action
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1 border-slate-300 text-slate-700">
      <Server className="w-3 h-3" />
      MCP
    </Badge>
  );
}

function ConnectionCard({
  conn,
  className,
  onEdit,
  onDelete,
  onToggle,
}: {
  conn: BusinessDataConnection;
  className?: string;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    const result = await refreshBusinessDataConnection(conn.id);
    setRefreshing(false);
    if (result.ok) {
      toast.success(`Refreshed ${conn.name}.`);
    } else {
      toast.error(result.error ?? "Refresh failed.");
    }
  };

  const subtitle = conn.last_error
    ? <span className="text-destructive">Last sync failed: {conn.last_error}</span>
    : conn.connection_type === "url_feed"
      ? <>Last sync: {timeAgo(conn.last_fetched_at)}{conn.last_payload_size_bytes ? ` · ${(conn.last_payload_size_bytes / 1024).toFixed(1)} KB` : ""}</>
      : conn.connection_type === "rest_action"
        ? <>AI performs this action · {conn.action_mode === "auto" ? "runs automatically" : conn.action_mode === "off" ? "turned off" : "needs your approval"}</>
        : conn.connection_type === "mcp_remote"
          ? <>{Array.isArray(conn.last_payload) ? `${conn.last_payload.length} tool${conn.last_payload.length === 1 ? "" : "s"}` : "discovering tools…"} · {conn.action_mode === "auto" ? "actions run automatically" : conn.action_mode === "off" ? "turned off" : "actions need approval"}</>
          : <>AI queries this endpoint on demand</>;

  return (
    <div className={`flex items-center justify-between gap-4 p-4 border rounded-xl bg-card shadow-sm ${className ?? ""}`}>
      <div className="flex items-center gap-4 min-w-0">
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-50 text-purple-600 shrink-0">
          <Database className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm truncate">{conn.name}</h3>
            <TypePill type={conn.connection_type} />
            {conn.is_active && !conn.last_error && (
              <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white border-transparent">
                Connected
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-3">
        {conn.connection_type === "url_feed" && conn.is_active && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRefresh} disabled={refreshing} title="Refresh now">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </Button>
        )}
        <Switch checked={conn.is_active} onCheckedChange={onToggle} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive">Remove</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function AddDataSourceRow({
  hasConnections,
  onClick,
}: {
  hasConnections: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 last:pb-0">
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <Plug className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {hasConnections
              ? "Add another data source"
              : "Connect your business data"}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Hook up a JSON feed or REST API so the AI sees your real-time data.
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 hover:bg-muted"
        onClick={onClick}
        aria-label={
          hasConnections ? "Add another data source" : "Connect business data"
        }
      >
        <Plus className="h-5 w-5 text-muted-foreground" />
      </Button>
    </div>
  );
}

export default function BusinessDataSection() {
  const { connections, isLoading, toggle, remove } = useBusinessDataConnections();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BusinessDataConnection | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BusinessDataConnection | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (conn: BusinessDataConnection) => {
    setEditing(conn);
    setDialogOpen(true);
  };

  return (
    <section aria-labelledby="business-data-connections-heading">
      <div className="mb-2 flex items-center gap-4">
        <h2 id="business-data-connections-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
          Business data
        </h2>
        <div className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
      <div className="divide-y divide-border">
        {isLoading ? (
          <div className="flex justify-center pb-0 pt-0">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : connections.length === 0 ? (
          <PlanFeatureGate feature="custom_connections" variant="compact">
            <AddDataSourceRow hasConnections={false} onClick={openCreate} />
          </PlanFeatureGate>
        ) : (
          <>
            {connections.map((conn) => (
              <ConnectionCard
                key={conn.id}
                conn={conn}
                className="!rounded-none !border-0 !bg-transparent !px-0 !py-2.5 !shadow-none last:!pb-0"
                onEdit={() => openEdit(conn)}
                onDelete={() => setPendingDelete(conn)}
                onToggle={() => toggle.mutate(conn.id)}
              />
            ))}
            <PlanFeatureGate feature="custom_connections" variant="compact">
              <AddDataSourceRow hasConnections onClick={openCreate} />
            </PlanFeatureGate>
          </>
        )}
      </div>

      <BusinessDataConnectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{pendingDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The AI will stop using this data source immediately. You can reconnect it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete) {
                  remove.mutate(pendingDelete.id, {
                    onSettled: () => setPendingDelete(null),
                  });
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
